/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { AlertTriangle, RefreshCw, WifiOff } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';

export const PostSkeleton = () => (
  <div className="p-4 rounded-2xl bg-white border border-border" data-testid="loading-skeleton">
    <div className="flex items-center gap-3">
      <Skeleton className="h-10 w-10 rounded-full" />
      <div className="space-y-2">
        <Skeleton className="h-3 w-40" />
        <Skeleton className="h-2 w-24" />
      </div>
    </div>
    <Skeleton className="h-4 w-full mt-4" />
    <Skeleton className="h-4 w-3/4 mt-2" />
  </div>
);

export const EmptyState = ({ title, subtitle, icon, action }) => (
  <div className="flex flex-col items-center justify-center text-center py-16 px-6" data-testid="empty-state">
    <div className="h-16 w-16 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
      {icon}
    </div>
    <h3 className="font-display font-semibold text-lg">{title}</h3>
    {subtitle && <p className="text-sm text-muted-foreground mt-1 max-w-sm">{subtitle}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

/**
 * ErrorState — fallback elegante per errori di caricamento (rete, server, AI locale).
 * Non rompe mai l'interfaccia: mostra il messaggio e un'azione "Riprova".
 */
export const ErrorState = ({ title = 'Qualcosa non ha funzionato', message, onRetry, retryLabel = 'Riprova', offline = false, compact = false, testId = 'error-state' }) => (
  <div className={`rounded-xl border border-dashed border-border bg-card text-center ${compact ? 'p-4' : 'p-8'}`} role="alert" data-testid={testId}>
    <div className={`mx-auto rounded-full flex items-center justify-center mb-3 ${offline ? 'bg-warning/10 text-warning' : 'bg-destructive/10 text-destructive'} ${compact ? 'h-9 w-9' : 'h-12 w-12'}`}>
      {offline ? <WifiOff className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
    </div>
    <h3 className="font-display font-semibold text-sm">{offline ? 'Sei offline' : title}</h3>
    {message && <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto" data-testid={`${testId}-message`}>{message}</p>}
    {onRetry && (
      <Button variant="outline" size="sm" onClick={onRetry} className="mt-3 rounded-xl" data-testid={`${testId}-retry`}>
        <RefreshCw className="h-3.5 w-3.5" /> {retryLabel}
      </Button>
    )}
  </div>
);

export default PostSkeleton;
